import { useEffect, useRef, useState } from "react";

const DEFAULT_ANIMATION_DURATION_MS = 1600;

// Module-level helper animates a numeric count-up. Returning early for
// non-numeric values keeps the effect callback tiny.
function animateCount(target, duration, setDisplay) {
  const start = performance.now();
  const tick = (t) => {
    const p = Math.min((t - start) / duration, 1);
    const eased = 1 - Math.pow(1 - p, 3);
    setDisplay(Math.floor(eased * target));
    if (p < 1) requestAnimationFrame(tick);
    else setDisplay(target);
  };
  requestAnimationFrame(tick);
}

export default function AnimatedCounter({ value, suffix = "", duration = DEFAULT_ANIMATION_DURATION_MS, className = "" }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const handleIntersect = (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || started.current) return;
        started.current = true;
        const numeric = Number(value);
        if (Number.isNaN(numeric)) {
          setDisplay(value);
        } else {
          animateCount(numeric, duration, setDisplay);
        }
      });
    };

    const io = new IntersectionObserver(handleIntersect, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
    // `animateCount` is a module-level helper and `IntersectionObserver` is a
    // browser global; neither belongs in the dependency array despite what the
    // exhaustive-deps rule reports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return (
    <span ref={ref} className={className} data-testid="animated-counter">
      {display}
      {suffix}
    </span>
  );
}
